import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { KeyValueDescriptionType } from '@models/common';
import { DataListFieldPropertiesModel, DataListType, FieldModel } from '@models/module/fields/main';
import { GridViewModel } from '@models/module/grid';
import { HttpService } from '@shared/services';

import {
  DataListAggregateFormulas,
  DataListComplexFormulaModel,
  ModuleDataList,
  ModuleDataListSync,
  ModuleDataListWithFields,
  ReportFieldModel,
} from './module-config.model';

@Injectable({ providedIn: 'root' })
export class ModuleDataListsService {
  private readonly http = inject(HttpService);

  private options(moduleId: number, params: Record<string, unknown> = {}) {
    return {
      useLoader: false,
      httpOptions: { params: this.http.buildHttpParams(params), headers: { moduleId: moduleId.toString() } },
    };
  }

  searchDataLists(moduleId: number, dataListType?: DataListType, relationId?: number): Observable<ModuleDataList[]> {
    return this.http
      .post<
        GridViewModel<ModuleDataList[]>
      >('_api/DataList/SearchDataLists', {}, this.options(moduleId, { dataListType, relationId }))
      .pipe(map(result => result?.data ?? []));
  }

  startScreens(moduleId: number, relationId?: number): Observable<KeyValueDescriptionType<number>[]> {
    return this.http
      .get<KeyValueDescriptionType<number>[]>('_api/DataList/DictStartScreens', this.options(moduleId, { relationId }))
      .pipe(map(screens => screens ?? []));
  }

  createDataList(moduleId: number, screenId: number, relationId?: number): Observable<number> {
    return this.http.post<number>(
      '_api/DataList/Create',
      { ...(relationId ? { relationId } : {}), screenId, moduleDataListTypeId: DataListType.Module },
      this.options(moduleId),
    );
  }

  renameDataList(moduleId: number, id: number, name: string): Observable<number> {
    // Create is an upsert: an existing ID updates the name without synchronizing fields.
    return this.http.post<number>('_api/DataList/Create', { id, name }, this.options(moduleId));
  }

  duplicateDataList(moduleId: number, id: number, screenId: number): Observable<number> {
    return this.http.put<number>('_api/DataList/DuplicateDataList', {}, this.options(moduleId, { id, screenId }));
  }

  deleteDataList(moduleId: number, id: number) {
    return this.http.put('_api/DataList/Delete', {}, this.options(moduleId, { id }));
  }

  searchFields(moduleId: number, dataListId: number, relationId?: number): Observable<DataListFieldPropertiesModel[]> {
    return this.http
      .post<
        GridViewModel<DataListFieldPropertiesModel[]>
      >('_api/DataList/Search', {}, this.options(moduleId, { dataListId, relationId }))
      .pipe(map(result => result?.data ?? []));
  }

  moduleFields(moduleId: number, dataListId: number, relationId?: number): Observable<FieldModel[]> {
    return this.http
      .post<
        GridViewModel<FieldModel[]>
      >('_api/DataList/ModuleFields', {}, this.options(moduleId, { dataListId, relationId }))
      .pipe(map(result => result?.data ?? []));
  }

  syncFields(moduleId: number, body: ModuleDataListSync): Observable<ModuleDataListWithFields> {
    return this.http.put<ModuleDataListWithFields>('_api/DataList/SyncFieldToDataList', body, this.options(moduleId));
  }

  syncAggregates(moduleId: number, dataListId: number, formulas: DataListAggregateFormulas[]) {
    return this.http.put(
      '_api/DataListAggregateFormula/SyncAggregateFormulas',
      formulas,
      this.options(moduleId, { dataListId }),
    );
  }

  complexFormulas(moduleId: number, dataListId: number): Observable<DataListComplexFormulaModel[]> {
    return this.http
      .get<
        DataListComplexFormulaModel[]
      >('_api/DataListComplexFormula/AllComplex', this.options(moduleId, { dataListId }))
      .pipe(map(formulas => formulas ?? []));
  }

  addComplexFormula(moduleId: number, dataListId: number, formula: DataListComplexFormulaModel): Observable<number> {
    return this.http.post<number>('_api/DataListComplexFormula/Add', formula, this.options(moduleId, { dataListId }));
  }

  editComplexFormula(moduleId: number, dataListId: number, formula: DataListComplexFormulaModel) {
    return this.http.put('_api/DataListComplexFormula/Edit', formula, this.options(moduleId, { dataListId }));
  }

  syncComplexFormulas(moduleId: number, dataListId: number, formulas: DataListComplexFormulaModel[]) {
    return this.http.put('_api/DataListComplexFormula/SyncFormulas', formulas, this.options(moduleId, { dataListId }));
  }

  complexFormulaFields(moduleId: number): Observable<KeyValueDescriptionType<number>[]> {
    return this.http
      .get<
        KeyValueDescriptionType<number>[]
      >('_api/FieldDefinition/DataListComplexFormulaFieldsDict', this.options(moduleId))
      .pipe(map(fields => fields ?? []));
  }

  reportFields(moduleId: number): Observable<ReportFieldModel[]> {
    return this.http
      .get<ReportFieldModel[]>('_api/FieldDefinition/AvailableForReportsFields', this.options(moduleId))
      .pipe(map(fields => fields ?? []));
  }

  syncReportFields(moduleId: number, fieldIds: number[]) {
    return this.http.put('_api/FieldDefinition/SyncFieldsOnReport', fieldIds, this.options(moduleId));
  }
}
